import { useDemoMode } from '@/context/DemoModeContext';

type DemoOnlyProps = {
  children: React.ReactNode;
  /** Optional: show fallback content in production mode */
  fallback?: React.ReactNode;
};

/**
 * Renders children only when in demo mode.
 * Use this to wrap features that should only be visible in demo mode.
 *
 * @example
 * <DemoOnly>
 *   <NewDemoFeature />
 * </DemoOnly>
 *
 * @example with fallback
 * <DemoOnly fallback={<RealFeature />}>
 *   <MockFeature />
 * </DemoOnly>
 */
export const DemoOnly = ({ children, fallback = null }: DemoOnlyProps) => {
  const { isDemoMode } = useDemoMode();

  if (!isDemoMode) return <>{fallback}</>;
  return <>{children}</>;
};

/**
 * Renders children only when NOT in demo mode (production mode).
 * Use this to hide features in demo mode.
 *
 * @example
 * <ProductionOnly>
 *   <RealPaymentButton />
 * </ProductionOnly>
 */
export const ProductionOnly = ({ children, fallback = null }: DemoOnlyProps) => {
  const { isDemoMode } = useDemoMode();

  if (isDemoMode) return <>{fallback}</>;
  return <>{children}</>;
};

export default DemoOnly;
